import ErrorHandler from 'common/error/components/error-handler';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import Timeline from '../components/timeline';
import { fetchTimeline, fetchTimelineFuture, fetchTimelinePast } from './timeline-actions';
import { getFilters, getObjects, getStatusses, getTimeline, getTypes } from './timeline-selectors';

const parseFilters = ({ selectedStatus, selectedFilter, selectedObject, selectedType }) => {
  const validStatus = selectedStatus && selectedStatus !== 'all';
  const validFilter = selectedFilter && selectedFilter !== 'none';
  const validObject = selectedObject && selectedObject !== 'all';
  const validType = selectedType && selectedType !== 'all';

  return {
    object: validObject ? selectedObject : null,
    status: validStatus ? selectedStatus : null,
    filter: validFilter ? selectedFilter : null,
    selectedType: validType ? selectedType : null,
  };
};

const TimelineContainer = ({
  id,
  type,
  futureDate,
  pastDate,
  activeFilter,
  activeFilterValue,
  fetch,
  fetchFuture,
  fetchPast,
  jumpToDate,
  error,
  ...rest
}) => {
  const [selectedState, setSelectedState] = useState({
    selectedStatus: activeFilter === 'status' ? activeFilterValue : 'all',
    selectedFilter: activeFilter === 'filter' ? activeFilterValue : 'none',
    selectedObject: 'all',
    selectedType: 'all',
  });

  const fetchTimelineData = (timeValue = '') => {
    const filters = parseFilters(selectedState);
    if (timeValue === 'p') {
      fetchPast({ id, type, date: pastDate, ...filters });
    } else if (timeValue === 'f') {
      fetchFuture({ id, type, date: futureDate, ...filters });
    } else {
      fetch({ id, type, ...filters });
    }
  };

  const handleSelectionChange = (key, value) => {
    setSelectedState(s => ({
      ...s,
      [key]: value,
    }));
  };

  useDeepCompareEffect(() => {
    if (id && type && !isObjectNullOrEmpty(selectedState)) {
      fetchTimelineData();
    }
  }, [id, type, selectedState]);

  return (
    <>
      <ErrorHandler type='notification' error={error} />
      <Timeline
        id={id}
        type={type}
        loadMoreFuture={() => fetchTimelineData('f')}
        loadMorePast={() => fetchTimelineData('p')}
        handleStatusChange={e => handleSelectionChange('selectedStatus', e.target.value)}
        handleFilterChange={e => handleSelectionChange('selectedFilter', e.target.value)}
        handleObjectChange={e => handleSelectionChange('selectedObject', e.target.value)}
        handleTypeChange={e => handleSelectionChange('selectedType', e)}
        futureItemsAvailable={(futureDate || false) && true}
        pastItemsAvailable={(pastDate || false) && true}
        reload={fetchTimelineData}
        jumpToDate={decodeURIComponent(jumpToDate)}
        {...selectedState}
        {...rest}
      />
    </>
  );
};

TimelineContainer.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  fetch: PropTypes.func.isRequired,
  fetchFuture: PropTypes.func.isRequired,
  fetchPast: PropTypes.func.isRequired,
  futureDate: PropTypes.string,
  pastDate: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  filters: PropTypes.array,
  statusses: PropTypes.array,
  objects: PropTypes.array,
  types: PropTypes.array,
  jumpToDate: PropTypes.string,
  activeFilter: PropTypes.string,
  activeFilterValue: PropTypes.string,
  error: PropTypes.object,
  menuVisible: PropTypes.bool,
  activeContext: PropTypes.string,
  hasAnnouncementBanner: PropTypes.bool,
  canCreateTicket: PropTypes.bool,
  canCreateOrder: PropTypes.bool,
  renderConnectionStatus: PropTypes.func,
  renderMenu: PropTypes.func,
  isInactive: PropTypes.bool,
  isLoadingPast: PropTypes.bool,
};

const mapStateToProps = state => ({
  data: getTimeline(state),
  isLoading: state.timeline.isLoading,
  error: state.timeline.error,
  futureDate: state.timeline.futureDate,
  pastDate: state.timeline.pastDate,
  filters: getFilters(state),
  statusses: getStatusses(state),
  objects: getObjects(state),
  types: getTypes(state),
  menuVisible: state.header.menuVisible,
  activeContext: state.navigation.activeContext,
  hasAnnouncementBanner: state.user.hasAnnouncementBanner,
  isInactive: state.timeline.isInactive,
  isLoadingPast: state.timeline.isLoadingPast,
});

const mapDispatchToProps = {
  fetch: fetchTimeline,
  fetchPast: fetchTimelinePast,
  fetchFuture: fetchTimelineFuture,
};

export default connect(mapStateToProps, mapDispatchToProps)(TimelineContainer);
